import api from '@services/api';
import {
  LoginMethodDto,
  OtpActivateRequest,
  OtpInitiateEmailRequest,
  Profile,
  ProfileApi,
  ProfileStatusDto,
  ProfileUpdateDto,
  PromoCodeRequest,
  PromoCodeResponse,
  SegmentsApi,
} from '@services/generated';
import { handleErrors } from '@services/helpers';
import { rootApi } from '@store/root-api';
import appHelpers from '@utils/app-urls';

type ActiveCustomerRequest = {
  revixRoundUp?: boolean;
  acceptedTerms: string[];
};
const API_URL = appHelpers.getEndpointUrl();

const profileClient = new ProfileApi(
  undefined,
  new URL(API_URL, process.env.REACT_API_URL).href,
  api,
);

const segmentsClient = new SegmentsApi(
  undefined,
  new URL(API_URL, process.env.REACT_API_URL).href,
  api,
);
export const profileApi = rootApi.injectEndpoints({
  endpoints: builder => ({
    getProfile: builder.query<Profile, void>({
      queryFn: data =>
        handleErrors(async () => {
          return await profileClient.get();
        }),
      providesTags: [{ type: 'UserProfile' }],
    }),
    getProfileStatus: builder.query<ProfileStatusDto, void>({
      queryFn: data =>
        handleErrors(async () => {
          return await profileClient.status();
        }),
      providesTags: [{ type: 'UserProfileStatus' }],
    }),
    validatePromoCode: builder.query<PromoCodeResponse, PromoCodeRequest>({
      queryFn: request =>
        handleErrors(() => profileClient.validatePromoCode(request)),
    }),
    updateDetails: builder.mutation<Profile, ProfileUpdateDto>({
      queryFn: request =>
        handleErrors(() => profileClient.updateDetails(request)),
      invalidatesTags: [{ type: 'UserProfile' }, { type: 'UserProfileStatus' }],
    }),
    activateProfile: builder.mutation<ProfileStatusDto, ActiveCustomerRequest>({
      queryFn: request =>
        handleErrors(() =>
          profileClient.activate(request.revixRoundUp, request.acceptedTerms),
        ),
      invalidatesTags: [
        { type: 'UserProfile' },
        { type: 'UserProfileStatus' },
        { type: 'UserTerms', id: 'LIST' },
      ],
    }),
    getLoginMethods: builder.query<LoginMethodDto[], void>({
      queryFn: request => handleErrors(() => profileClient.loginMethods()),
      providesTags: [{ type: 'LoginMethods' }],
    }),
    initiateEmailOtp: builder.mutation<void, OtpInitiateEmailRequest>({
      queryFn: request =>
        handleErrors(() => profileClient.initiateEmailOtp(request)),
    }),
    activateEmailOtp: builder.mutation<void, OtpActivateRequest>({
      queryFn: request =>
        handleErrors(() => profileClient.activateEmailOtp(request)),
    }),
    changeEmailNotificationEnabled: builder.mutation<Profile, boolean>({
      queryFn: request =>
        handleErrors(() => {
          if (request) {
            return profileClient.enableLoginNotification();
          } else {
            return profileClient.disableLoginNotification();
          }
        }),
      invalidatesTags: [{ type: 'UserProfile' }, { type: 'UserProfileStatus' }],
      onQueryStarted(enabled, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          profileApi.util.updateQueryData('getProfile', undefined, draft => {
            return {
              ...draft,
              loginNotification: enabled,
            };
          }),
        );

        queryFulfilled.catch(patchResult.undo);
      },
    }),
    getAccessToSegment: builder.query<boolean, string>({
      queryFn: segmentCode =>
        handleErrors(async () => {
          return await segmentsClient.isCustomerInSegment(segmentCode);
        }),
      providesTags: (result, error, segmentCode) => [
        { type: 'Segments', id: segmentCode },
      ],
    }),
  }),
});

export const {
  useGetProfileQuery,
  useUpdateDetailsMutation,
  useActivateProfileMutation,
  useValidatePromoCodeQuery,
  useGetLoginMethodsQuery,
  useChangeEmailNotificationEnabledMutation,
  useGetAccessToSegmentQuery,
} = profileApi;
